import styled from 'styled-components';
import { variant } from 'styled-system';
import { Box } from '../../../../../../../../../../CommonComponents/OPL/Box/Box';
import { Card as BaseCard } from '../../../../../../../../../../CommonComponents/OPL/Card/Card';
import { BorderProps } from '../../../../../../../../../../theme/styledProps';
import { VariantProps } from '../../FlowChart.meta';

export const cardWidth = 200;

export const Card = styled(BaseCard)(
  ({ theme }) => {
    return {
      width: `${cardWidth}px`,
      height: '210px',
      borderTop: `${theme.space['4']}px`,
      borderRight: '0',
      borderBottom: '0',
      borderLeft: '0',
      borderStyle: 'solid',

      '@media print': {
        borderRight: `1px solid ${theme.colors.neutral['200']}`,
        borderBottom: `1px solid ${theme.colors.neutral['200']}`,
        borderLeft: `1px solid ${theme.colors.neutral['200']}`,
        height: '180px',
      },
    };
  },

  variant<BorderProps, VariantProps>({
    prop: 'color',
    variants: {
      neutral: {
        borderColor: 'neutral.100',
      },
      principal: {
        borderColor: 'blue.400',
      },

      spouse: {
        borderColor: 'green.700',
      },

      beneficiaries: {
        borderColor: 'indigo.700',
      },

      taxesAndFees: {
        borderColor: 'neutral.500',
      },

      charityBeneficiaries: {
        borderColor: 'yellow.300',
      },
    },
  }),
);

export const PrintVerticalLine = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    '&:after': {
      content: '""',
      minHeight: '20px',
      width: '1px',
      backgroundColor: theme.colors.neutral[400],
      flexGrow: 1,
    },
  };
});

interface VerticalLineProps {
  firstConnectorLine?: boolean;
  lastConnectorLine?: boolean;
}

export const VerticalLine = styled(Box)<VerticalLineProps>(
  ({ theme, firstConnectorLine = false, lastConnectorLine = false }) => {
    return {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',

      ...(!firstConnectorLine && !lastConnectorLine
        ? {
            '&:not(:first-child):not(:last-child), &:only-child': {
              '&:after': {
                content: '""',
                minHeight: '50px',
                width: '1px',
                backgroundColor: theme.colors.neutral[400],
                flexGrow: 1,
              },
            },
          }
        : {}),

      ...(firstConnectorLine && !lastConnectorLine
        ? {
            '&:after': {
              content: '""',
              minHeight: '50px',
              width: '1px',
              backgroundColor: theme.colors.neutral[400],
              flexGrow: 1,
            },
          }
        : {}),
    };
  },
);

export const HorizontalLine = styled(Box)<{ isFirst?: boolean; items: number }>(
  ({ theme, isFirst, items }) => {
    if (!isFirst) {
      return {
        display: 'flex',
        justifyContent: 'center',
      };
    }

    const cardGap = 8;

    return {
      display: 'flex',
      justifyContent: 'center',
      position: 'relative',

      '&:before': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        height: '1px',
        backgroundColor: theme.colors.neutral[400],
        flexGrow: 1,
        width: `calc((${cardWidth}px * ${items - 1}) + (${cardGap}px * ${items - 1}))`,
      },
    };
  },
);

export const PrintGroupLine = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${cardWidth}px ${cardWidth}px`,
  justifyContent: 'center',
  gap: `${theme.space['16']}px`,
  position: 'relative',
  paddingTop: `${theme.space[16]}px`,

  '& > *:nth-child(odd):last-child': {
    gridColumn: '1 / -1',
    margin: '0 auto',
  },

  '&:before': {
    content: '""',
    position: 'absolute',
    height: `${theme.space[16]}px`,
    width: '100%',
    borderRight: `1px solid ${theme.colors.neutral[400]}`,
    borderLeft: `1px solid ${theme.colors.neutral[400]}`,
    borderTop: `1px solid ${theme.colors.neutral[400]}`,
  },
}));

export const GroupLine = styled(Box)<{ isVisible: boolean }>(({ theme, isVisible }) => {
  if (!isVisible) {
    return {
      display: 'flex',
      gridGap: theme.space[8],
      justifyContent: 'center',
    };
  }

  return {
    display: 'flex',
    gridGap: theme.space[8],
    justifyContent: 'center',
    position: 'relative',
    padding: `0 ${theme.space[32]}px`,
    margin: `${theme.space[32]}px auto 0 auto`,

    '&:before': {
      content: '""',
      position: 'absolute',
      height: `${theme.space[32]}px`,
      width: '100%',
      borderRight: `1px solid ${theme.colors.neutral[400]}`,
      borderLeft: `1px solid ${theme.colors.neutral[400]}`,
      borderTop: `1px solid ${theme.colors.neutral[400]}`,
      top: `-${theme.space[32]}px`,
    },
  };
});

export const PrintNodesContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `${cardWidth}px ${cardWidth}px`,
  justifyContent: 'center',
  gap: `${theme.space['16']}px`,

  '& > *:nth-child(odd):last-child': {
    gridColumn: '1 / -1',
    margin: '0 auto',
  },
}));
